import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
} from "../../ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { useState, useRef, useCallback } from "react";
import { Skeleton } from "../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { useRouter } from "next/router";
import { ResultHeading } from "./ResultHeading";
import LightningSvg from "../../../../icons/Lightning";

export type Option = Record<"value" | "label", string> & Record<string, string>;

export type Options = {
  group: string;
  data: Option[];
};

type AutoCompleteProps = {
  options: Options[];
  emptyMessage: string;
  value?: Option;
  onValueChange?: (value: Option) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onInputValueChange: (value: any) => void;
  inputValue: string;
  className: string;
  city: any;
};

export const AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isLoading = false,
  onInputValueChange,
  inputValue,
  className,
  city,
}: AutoCompleteProps) => {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>(value as Option);

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, [selected]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      router.push(selectedOption.value);

      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange]
  );

  return (
    <CommandPrimitive shouldFilter={false} className="w-full">
      <CommandInput
        ref={inputRef}
        value={inputValue}
        onValueChange={onInputValueChange}
        onBlur={handleBlur}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(className)}
      />
      <div className="relative mt-1">
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 absolute top-7 z-50 w-full rounded-xl bg-white outline-none",
            isOpen ? "block" : "hidden"
          )}
        >
          <CommandList
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            className="rounded-lg ring-1 ring-slate-200"
          >
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {options.length > 0 && !isLoading
              ? options.map((option: Options) => {
                  return (
                    <CommandGroup
                      key={option.group}
                      heading={
                        <ResultHeading city={city} groupName={option.group} />
                      }
                    >
                      {option?.data?.map((item: Option) => {
                        return (
                          <CommandItem
                            key={item.value}
                            value={item.label}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onSelect={() => handleSelectOption(item)}
                            className={cn(
                              "flex w-full text-web-body-lg hover:bg-gray-50 text-brand-textBlack pt-[19px] pb-[18px] px-[28px] items-center",
                              option.group ===
                                "other_results_in_diff_asset_type" &&
                                "bg-brand-lightOrange hover:bg-brand-lightOrange justify-between"
                            )}
                          >
                            <span className="flex flex-col">
                              <span className="capitalize">{item.label}</span>
                              {option.group ===
                                "other_results_in_diff_asset_type" && (
                                <span className="text-web-body-sm">
                                  In {item.category} property
                                </span>
                              )}
                            </span>
                            {option.group ===
                              "other_results_in_diff_asset_type" && (
                              <LightningSvg
                                width="32"
                                height="32"
                                className=""
                              />
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })
              : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
